import clsx from 'clsx';
import React, { useEffect } from 'react';

import { IconX } from '@tabler/icons-react';

import Button from '../Button/Button';
import SkeletonLoader from '../SkeletonLoader';

type TModalPosition = 'center' | 'right';

type TModalButtonProps = {
  label?: string;
  onClick?: () => void;
  variant?: 'primary' | 'outline' | 'link';
  desructive?: boolean;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  isRightIcon?: boolean;
  href?: string;
};

interface IPopupProps {
  setIsOpen: (isOpen: boolean) => void;
  headercontent?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
  isFooter?: boolean;
  isHeader?: boolean;
  heading?: string;
  childClassName?: string;
  footerContent?: React.ReactNode;
  position?: TModalPosition;
  cancelButtonProps?: TModalButtonProps;
  confirmButtonProps?: TModalButtonProps;
  buttonClassName?: string;
  loadingModal?: boolean;
}

const CustomModal: React.FC<IPopupProps> = ({
  setIsOpen,
  headercontent,
  children,
  className,
  isFooter,
  onClose,
  isHeader,
  heading,
  childClassName,
  confirmButtonProps,
  cancelButtonProps,
  footerContent,
  position = 'center',
  buttonClassName,
  loadingModal = false,
}) => {
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        onClose?.();
      }
    };

    (document.activeElement as HTMLElement)?.blur();
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [setIsOpen, onClose]);

  const isRightModal = position === 'right';

  return (
    <div
      className={clsx(
        'fixed inset-0 z-50 bg-[#00000080]/75 duration-300',
        // Center modal
        position === 'center' && 'flex items-center justify-center',
        // Right modal
        isRightModal && 'flex justify-end'
      )}
      onClick={() => {
        setIsOpen(false);
        onClose?.();
      }}
    >
      <div
        className={clsx(
          'bg-white shadow-md overflow-hidden',
          /*CENTER MODAL*/
          position === 'center' && 'rounded-xl max-h-[90vh] overflow-y-auto',
          /*RIGHT MODAL */
          isRightModal &&
            `w-full rounded-2xl sm:w-150 h-[calc(100vh-3rem)] flex flex-col m-6`,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {isHeader && (
          <div className="border-b flex justify-between items-center border-neutral-200 p-4 sticky top-0 bg-white z-10">
            {loadingModal ? (
              <SkeletonLoader className="w-1/2 h-7 rounded-lg" />
            ) : heading ? (
              <p className="text-neutral-primary font-medium leading-7 text-lg">
                {heading}
              </p>
            ) : (
              headercontent
            )}

            {loadingModal ? (
              <SkeletonLoader className="w-8 h-8 rounded-sm" />
            ) : (
              <IconX
                size={24}
                onClick={() => {
                  setIsOpen(false);
                  onClose?.();
                }}
                className="hover:scale-125 hover:bg-white  text-neutral-pressed  w-8 h-8 p-1.5 rounded-sm bg-neutral-light-50 cursor-pointer"
              />
            )}
          </div>
        )}

        {/* children */}
        <div
          className={clsx(
            'w-full flex flex-col items-center p-6',

            position === 'right' && 'flex-1 overflow-y-auto',

            childClassName
          )}
        >
          {children}
        </div>

        {/* Footer */}
        {isFooter && (
          <div className="sticky bottom-0 z-10 bg-white border-t border-neutral-200">
            <div className="flex items-center gap-4 p-4">
              {footerContent && <div className="flex-1">{footerContent}</div>}

              {/* buttons */}
              <div
                className={clsx(
                  'flex items-center justify-between w-full',
                  buttonClassName
                )}
              >
                {cancelButtonProps?.label &&
                  (loadingModal ? (
                    <SkeletonLoader className="w-30 h-10 rounded-lg me-auto" />
                  ) : (
                    <Button
                      {...cancelButtonProps}
                      variant={cancelButtonProps.variant ?? 'outline'}
                      className={clsx('min-w-24', cancelButtonProps.className)}
                    />
                  ))}

                {confirmButtonProps?.label &&
                  (loadingModal ? (
                    <SkeletonLoader className="w-36 h-10 rounded-lg ms-auto" />
                  ) : (
                    <Button
                      {...confirmButtonProps}
                      variant={confirmButtonProps.variant ?? 'primary'}
                      className={clsx('min-w-24', confirmButtonProps.className)}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomModal;
